<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h1 class="mb-2">
                    <span class="text-gradient">Program Analytics</span>
                </h1>
                <p class="text-muted mb-3">Deep analysis of Solana programs - transactions, fees, compute, and user activity</p>
                <div class="d-flex align-items-center gap-3 flex-wrap">
                    <date-range-picker
                        v-model="dateRange"
                        default-range="24h"
                        @change="handleDateRangeChange"
                    />
                    <granularity-toggle
                        v-model="granularityMode"
                        :interval="currentInterval"
                    />
                    <div class="system-toggle d-flex align-items-center gap-2">
                        <el-switch
                            v-model="showSystemPrograms"
                            size="small"
                            active-color="#8b5cf6"
                        />
                        <span class="toggle-label">Show System Programs</span>
                        <el-tooltip placement="top">
                            <template #content>
                                <div class="system-tooltip">
                                    Include Token Program, Associated Token Account,<br>
                                    Token-2022, and System Program in results
                                </div>
                            </template>
                            <i class="bi bi-info-circle text-muted"></i>
                        </el-tooltip>
                    </div>
                </div>
            </div>
        </div>

        <!-- Hero Stats -->
        <network-overview
            :summary="summary"
            :top-program="filteredPrograms[0]"
            :interval="currentInterval"
            :range="currentRange"
            :show-jito-completion="false"
        />

        <!-- Section: Transaction Activity -->
        <program-transaction-activity
            v-if="filteredPrograms.length > 0"
            :programs="filteredPrograms"
            :interval="currentInterval"
            :granularity-mode="granularityMode"
            @zoom="handleChartZoom"
        />

        <!-- Section: Fee Economics -->
        <program-fee-economics
            v-if="filteredPrograms.length > 0"
            :programs="filteredPrograms"
            :interval="currentInterval"
            :range="currentRange"
            :granularity-mode="granularityMode"
        />

        <!-- Section: Compute & Block Space -->
        <program-compute-metrics
            v-if="filteredPrograms.length > 0"
            :programs="filteredPrograms"
            :interval="currentInterval"
            :range="currentRange"
            :show-system-programs="showSystemPrograms"
            :granularity-mode="granularityMode"
        />

        <!-- Section: User Activity -->
        <program-user-activity
            v-if="filteredPrograms.length > 0"
            :programs="filteredPrograms"
            :interval="currentInterval"
            :granularity-mode="granularityMode"
        />

        <!-- Section: Jito MEV & Transaction Completion (moved down) -->
        <div class="section-divider">
            <span class="section-title">Network Health</span>
        </div>
        <network-overview
            :summary="summary"
            :top-program="filteredPrograms[0]"
            :interval="currentInterval"
            :range="currentRange"
            :show-hero-stats="false"
            :show-jito-completion="true"
        />

        <!-- Section: DeFi Activity -->
        <program-defi-activity
            v-if="dexProgramsData.length > 0"
            :dex-programs="dexProgramsData"
            :aggregators="aggregatorsData"
        />

        <!-- Section: Program Complexity -->
        <program-complexity
            v-if="filteredPrograms.length > 0"
            :programs="filteredPrograms"
        />

        <!-- Section: Full Program Table -->
        <div class="section-divider">
            <span class="section-title">Complete Program Breakdown</span>
        </div>
        <div class="analytics-row cols-1">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">All Programs</div>
                </div>
                <div class="panel-body">
                    <program-table
                        :programs="programsData"
                        :total-programs="totalProgramCount"
                        :loading="loading"
                        :initial-sort-field="sortField"
                        :initial-sort-dir="sortDir"
                        @page-change="handlePageChange"
                        @sort-change="handleSortChange"
                        @per-page-change="handlePerPageChange"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatCompact, formatCompute, formatSOL, formatPubkey } from '../composables/useFormatters';
import { systemPrograms } from '../composables/useChartConfig';
import { GRANULARITY_MODES } from '../composables/useGranularityToggle';

export default {
    name: 'ProgramAnalytics',

    data() {
        return {
            currentRange: '24h',
            currentInterval: '1h',
            granularityMode: GRANULARITY_MODES.TOTAL,
            dateRange: null, // For bidirectional sync with DateRangePicker
            showSystemPrograms: false, // Default to hiding system programs
            summary: {
                totalPrograms: 0,
                totalFees: 0,
                totalUsers: 0,
                totalCompute: 0
            },
            programsData: [],
            dexProgramsData: [],
            aggregatorsData: [],
            refreshInterval: null,
            // Pagination state
            loading: false,
            totalProgramCount: 0,
            currentPage: 1,
            perPage: 25,
            sortField: 'tx_count',
            sortDir: 'desc'
        };
    },

    computed: {
        // Filter out system programs when toggle is off
        filteredPrograms() {
            if (this.showSystemPrograms) {
                return this.programsData;
            }
            return this.programsData.filter(p => !systemPrograms.includes(p.program));
        }
    },

    mounted() {
        this.loadData();
        this.refreshInterval = setInterval(() => this.loadData(), 30000);
    },

    beforeUnmount() {
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.currentInterval = data.interval;
            this.currentPage = 1;
            this.loadData();
        },

        async loadData() {
            this.loading = true;
            try {
                const programParams = new URLSearchParams({
                    range: this.currentRange,
                    interval: this.currentInterval,
                    sortBy: this.sortField,
                    sortDir: this.sortDir,
                    page: this.currentPage,
                    per_page: this.perPage
                });

                const [programRes, dexRes] = await Promise.all([
                    fetch(`/analytics/api/program-metrics?${programParams}`),
                    fetch(`/analytics/api/defi-metrics?range=${this.currentRange}&limit=20`)
                ]);

                const programData = await programRes.json();
                const dexData = await dexRes.json();

                this.programsData = programData.programs || [];
                this.totalProgramCount = programData.totalPrograms || 0;
                this.dexProgramsData = dexData.dexPrograms || [];
                this.aggregatorsData = dexData.aggregators || [];

                this.updateSummary(programData);
            } catch (error) {
                console.error('Error loading analytics data:', error);
            } finally {
                this.loading = false;
            }
        },

        updateSummary(data) {
            this.summary.totalPrograms = data.totalPrograms || 0;
            this.summary.totalCompute = data.networkTotals?.compute || 0;
            this.summary.totalFees = data.networkTotals?.fees || 0;
            if (this.programsData.length > 0) {
                this.summary.totalUsers = this.programsData.reduce(
                    (sum, p) => sum + (p.metrics.uniqueUsers || 0), 0
                );
            }
        },

        // Pagination event handlers
        handlePageChange({ page, perPage }) {
            this.currentPage = page;
            this.perPage = perPage;
            this.loadData();
        },

        handleSortChange({ field, dir }) {
            this.sortField = field;
            this.sortDir = dir;
            this.currentPage = 1;
            this.loadData();
        },

        handlePerPageChange({ perPage }) {
            this.perPage = perPage;
            this.currentPage = 1;
            this.loadData();
        },

        handleChartZoom({ start, end, source }) {
            // Update the DateRangePicker to match the zoomed selection
            // This creates bidirectional sync between charts and the date picker
            this.dateRange = [new Date(start), new Date(end)];

            // The DateRangePicker's watch on modelValue will update its internal state,
            // and on the next change event, it will recalculate range/interval.
            // However, we want to trigger a reload immediately.

            // Calculate the appropriate interval based on time span
            const spanMs = end - start;
            const spanHours = spanMs / (1000 * 60 * 60);

            let newRange, newInterval;
            if (spanHours <= 1) {
                newRange = '1h';
                newInterval = '1m';
            } else if (spanHours <= 12) {
                newRange = '12h';
                newInterval = '1h';
            } else if (spanHours <= 24) {
                newRange = '24h';
                newInterval = '1h';
            } else if (spanHours <= 48) {
                newRange = '2d';
                newInterval = '1h';
            } else if (spanHours <= 96) {
                newRange = '4d';
                newInterval = '1d';
            } else if (spanHours <= 168) {
                newRange = '7d';
                newInterval = '1d';
            } else if (spanHours <= 336) {
                newRange = '14d';
                newInterval = '1d';
            } else {
                newRange = '30d';
                newInterval = '1d';
            }

            // Update state and reload data
            this.currentRange = newRange;
            this.currentInterval = newInterval;
            this.currentPage = 1;
            this.loadData();

            console.log(`Chart zoom from ${source}:`, {
                start: start.toISOString(),
                end: end.toISOString(),
                spanHours: spanHours.toFixed(1),
                newRange,
                newInterval
            });
        }
    }
};
</script>

<style scoped>
.hero-subtitle {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 0.25rem;
    font-family: 'JetBrains Mono', monospace;
}

.system-toggle {
    background: rgba(255, 255, 255, 0.05);
    padding: 0.5rem 1rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.toggle-label {
    font-size: 0.875rem;
    color: #9ca3af;
}

.system-tooltip {
    font-size: 0.8rem;
    line-height: 1.4;
}
</style>
